import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

@Component({
  selector: 'stupa-common-search-bar',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, ReactiveFormsModule],
  templateUrl: './common-search-bar.component.html',
  styleUrl: './common-search-bar.component.scss',
})
export class CommonSearchBarComponent implements OnChanges, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  /** Debounced mobile input stream — avoids firing API calls on every keystroke. */
  private readonly mobileSearchInput$ = new Subject<string>();

  @ViewChild('mobileInlineSearchInput')
  mobileInlineSearchInput?: ElementRef<HTMLInputElement>;

  screenWidth: any;

  /** Collapsed icon-only state: narrow viewports only; desktop always shows the full bar. */
  get isCollapsedInlineSearch(): boolean {
    return this.resolveViewportWidth() <= 1024 && !this.inputExpanded && !this.searchQuery;
  }

  @Input() searchQuery: any;
  @Input() searchPlaceholder: any = 'search_by_participant_name';
  @Input() is_grid: any;
  @Input() is_expandable: boolean = false;
  @Input() inputExpanded: boolean = false;
  
	@Output() searchChange = new EventEmitter<any>();
	/** Emitted when inline expand/collapse toggles; optional for parents (e.g. layout). Not emitted on each keystroke. */
	@Output() inputExpandedChange = new EventEmitter<boolean>();

  @Input() searchbarMaxWidth: string = '320px';
  /**
   * When false (default), parents are notified only below 600px so tablet
   * toolbars that hide chrome on `searchBarExpanded` keep their previous flow.
   * Opt in for pages whose icon-row collapse matches the inline bar (≤1024).
   */
  @Input() emitExpandedOnInlineLayout = false;

	@HostListener('window:resize')
	onResize() {
		const prevWidth = Number(this.screenWidth);
		this.screenWidth = globalThis.window.innerWidth;
		if (!this.inputExpanded || !this.wasExpandedNotifyActive(prevWidth)) {
			return;
		}
		if (!this.shouldNotifyExpandedChange()) {
			this.inputExpandedChange.emit(false);
		}
	}

  constructor() {
    this.screenWidth = globalThis.window.innerWidth;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['searchQuery']) {
      return;
    }
    // Expand when parent passes an initial/non-empty query (e.g. restored state).
    // Do not collapse when query becomes empty via backspace — only clearSearch() closes the bar.
		if (this.searchQuery && this.isInlineExpandLayoutActive()) {
			this.inputExpanded = true;
			this.emitInputExpandedChange(true);
		}
  }

  ngOnInit(): void {
    this.mobileSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.searchChange.emit(value);
      });
  }

	/** Notify parents when the inline bar expands/collapses; desktop must not collapse sibling content. */
	private emitInputExpandedChange(expanded: boolean): void {
		if (this.shouldNotifyExpandedChange()) {
			this.inputExpandedChange.emit(expanded);
		}
	}

  /** Parent layout (hide headings/tabs) only applies below this width by default. */
  private isMobileLayoutBreakpoint(): boolean {
    return this.resolveViewportWidth() < 600;
  }

  private shouldNotifyExpandedChange(): boolean {
    return this.emitExpandedOnInlineLayout
      ? this.isInlineExpandLayoutActive()
      : this.isMobileLayoutBreakpoint();
  }

  private wasExpandedNotifyActive(width: number): boolean {
    if (!Number.isFinite(width)) {
      return false;
    }
    return this.emitExpandedOnInlineLayout ? width <= 1024 : width < 600;
  }

  /** Inline collapse/expand only applies at this breakpoint; avoid emitting layout events on desktop. */
  private isInlineExpandLayoutActive(): boolean {
    return this.resolveViewportWidth() <= 1024;
  }

  private resolveViewportWidth(): number {
    const w = Number(this.screenWidth);
    if (Number.isFinite(w)) {
      return w;
    }
    if (globalThis.window === undefined) {
      return 1200;
    }
    return globalThis.window.innerWidth;
  }

  handleSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery = value;
    if (this.isInlineExpandLayoutActive()) {
      this.mobileSearchInput$.next(value);
      return;
    }
    this.searchChange.emit(value);
  }

  expandInput() {
    const wasExpanded = this.inputExpanded;
    this.inputExpanded = !this.inputExpanded;
    this.emitInputExpandedChange(this.inputExpanded);
    if (this.isInlineExpandLayoutActive() && !wasExpanded && this.inputExpanded) {
      setTimeout(() => this.mobileInlineSearchInput?.nativeElement?.focus());
    }
  }

  /** Whole pill expands when collapsed; ignore when expanded so input stays usable. */
  onInlineSearchShellClick(event: MouseEvent): void {
    if (!this.isInlineExpandLayoutActive() || !this.isCollapsedInlineSearch) {
      return;
    }
    if (event.defaultPrevented) {
      return;
    }
    this.expandInput();
  }

  onClearInlineSearchClick(event: MouseEvent): void {
    event.stopPropagation();
    this.clearSearch();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.inputExpanded = false;
    if (this.isInlineExpandLayoutActive()) {
      this.emitInputExpandedChange(false);
      this.mobileSearchInput$.next('');
      return;
    }
    this.searchChange.emit('');
  }
}